'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import './CommentsSection.scss';

interface Comment {
  id: string;
  text: string;
  createdAt: string;
  user: {
    id: string;
    username?: string;
    name?: string;
    image?: string;
  };
}

interface CommentsSectionProps {
  sketchId: string;
  initialComments?: Comment[];
}

export default function CommentsSection({
  sketchId,
  initialComments = [],
}: CommentsSectionProps) {
  const { data: session, status } = useSession();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      try {
        const response = await fetch(`/api/sketches/${sketchId}/comments`);
        const data = await response.json();
        
        if (data.success) {
          setComments(data.comments);
        }
      } catch (err) {
        console.error('Error loading comments:', err);
      }
    };

    loadComments();
  }, [sketchId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session) {
      setError('You must be logged in to comment');
      return;
    }

    if (newComment.trim().length === 0) {
      setError('Comment cannot be empty');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/sketches/${sketchId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: newComment.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setComments([data.comment, ...comments]);
        setNewComment('');
      } else {
        setError(data.error || 'Failed to post comment');
      }
    } catch (err) {
      console.error('Error posting comment:', err);
      setError('Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setIsDeleting(commentId);

    try {
      const response = await fetch(`/api/sketches/${sketchId}/comments/${commentId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setComments(comments.filter(c => c.id !== commentId));
      } else {
        alert(data.error || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="comments-section">
      <div className="comments-header">
        <h4>
          Comments {comments.length > 0 && <span className="comment-count">({comments.length})</span>}
        </h4>
        <button
          type="button"
          className="toggle-btn"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className="comments-content">
          {/* Comment Form */}
          {session ? (
            <form onSubmit={handleSubmit} className="comment-form">
              {error && <div className="error-message">{error}</div>}
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                maxLength={5000}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || newComment.trim().length === 0}
              >
                {isSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>
          ) : (
            <div className="login-prompt">
              Please sign in to comment
            </div>
          )}

          {/* Comments List */}
          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">
                No comments yet. Be the first to comment!
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-author">
                      {comment.user.image && (
                        <div className="author-avatar">
                          {comment.user.image.startsWith('<svg') ? (
                            <div 
                              className="avatar-svg"
                              dangerouslySetInnerHTML={{ __html: comment.user.image }}
                            />
                          ) : (
                            <img
                              src={comment.user.image}
                              alt={comment.user.username || comment.user.name || 'User'}
                            />
                          )}
                        </div>
                      )}
                      <span className="author-name">
                        {comment.user.username || comment.user.name || 'Anonymous'}
                      </span>
                      <span className="comment-date">{formatDate(comment.createdAt)}</span>
                    </div>
                    {session?.user?.id === comment.user.id && (
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleDelete(comment.id)}
                        disabled={isDeleting === comment.id}
                      >
                        {isDeleting === comment.id ? '...' : '🗑️'}
                      </button>
                    )}
                  </div>
                  <p className="comment-text">{comment.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
